import {Component, Inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {uniqueNamesOfAddedDrawingsValidator} from '@application/validators/unique-names-of-added-drawings-validator';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {DrawingImage} from '@domain/production-schedule/drawing-image';
import {DrawingInfo} from '@domain/production-schedule/drawing-info.interface';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {AssertionUtils, canShowErrorForErrorCodeAndFormControlForFormGroup, convertToCommercialUnitCentimeter, TranslateService, Unit} from '@vdw/angular-component-library';
import {forEach, isEmpty, isEqual} from 'lodash-es';
import {noop} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {BaseDrawingFormComponent} from '../base-drawing-form.component';
import {ConfigurationForm} from '../drawing-form';

@Component({
  selector: 'app-add-drawing-form',
  templateUrl: './add-drawing-form.component.html',
  styleUrls: ['../base-drawing-form.component.scss']
})
export class AddDrawingFormComponent extends BaseDrawingFormComponent implements OnChanges {
  @Input() public drawingImage: DrawingImage;
  @Input() public areImagesUploading: boolean;
  @Input() public drawingsToBeSaved: Drawing[];
  @Input() public drawingImageIndex: number;
  @Input() public canShowInvalidFormMessageError = false;
  @Input() public defaultDrawingShape: string;
  @Input() public overwriteDuplicateNames: boolean;
  public currentDrawingImageWillBeOverwritten: boolean;
  public nameHandlerSet: boolean = false;

  public constructor(
    @Inject(DRAWINGS) protected drawingsService: Drawings,
    formBuilder: FormBuilder,
    translate: TranslateService
  ) {
    super(drawingsService, formBuilder, translate);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (AssertionUtils.isNullOrUndefined(this.drawingForm)) {
      this.initializeDrawingForm();
    }

    this.setSyncValidatorForDrawingNameControl();

    if (!AssertionUtils.isNullOrUndefined(this.drawing) && changes.drawing?.currentValue !== changes.drawing?.previousValue) {
      this.initializeDrawingFormValuesFromDrawing();
    } else {
      if (!AssertionUtils.isNullOrUndefined(changes.drawingImage) && changes.drawingImage.firstChange) {
        this.initializeDrawingFormValuesFromDrawingImage();
      }

      if (!AssertionUtils.isNullOrUndefined(changes.defaultDrawingShape) && !AssertionUtils.isNullOrUndefined(this.defaultDrawingShape)) {
        this.drawingForm.controls.shape.setValue(this.defaultDrawingShape);
      }
    }

    if (
      !AssertionUtils.isNullOrUndefined(changes.areImagesUploading) &&
      !changes.areImagesUploading.currentValue &&
      !AssertionUtils.isNullOrUndefined(this.drawingImage) &&
      DrawingType.BMP === this.drawingImage.type
    ) {
      this.getDrawingInfo(this.drawingImage.name);
    }

    if (!this.nameHandlerSet) {
      this.subscribeToNameValueChanges();
      this.nameHandlerSet = true;
    }
    this.removeAsyncValidatorForDrawingNameControl();

    this.drawingForm.controls.name.markAsTouched();
  }

  public canShowImageLoader(): boolean {
    return this.areImagesUploading;
  }

  public canAddConfigurationForDrawing(): boolean {
    return !this.canShowImageLoader();
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.drawingForm);
  }

  public checkIfNameIsUnique(): boolean {
    return this.drawingForm.controls.name.hasError('nameInUse');
  }

  public addConfigurationToDrawing(): void {
    this.drawingForm.controls.configurations.push(
      this.formBuilder.group({
        quality: this.formBuilder.control(null, Validators.required),
        colorSet: this.formBuilder.control(null),
        yarnSet: this.formBuilder.control(null),
        mainColorsCreelPosition: this.formBuilder.control(null),
        borderColorsCreelPosition: this.formBuilder.control(null),
        image: this.formBuilder.control({id: this.drawingImage.id, data: this.drawingImage.data} as DrawingImage, Validators.required),
        nrColumnsForWeftSelection: this.drawingForm.getRawValue().nrColumnsForWeftSelection,
        threadByThread: this.drawingForm.getRawValue().threadByThread,
        heightInCm: this.formBuilder.control(0, Validators.min(0)),
        widthInCm: this.formBuilder.control(0, Validators.min(0))
      }) as ConfigurationForm
    );
    this.drawingForm.controls.nrColumnsForWeftSelection.disable();
  }

  public isDrawingTypeBmp(): boolean {
    return isEqual(this.drawingImage?.type, DrawingType.BMP);
  }

  private setSyncValidatorForDrawingNameControl(): void {
    this.drawingForm.controls.name.setValidators([uniqueNamesOfAddedDrawingsValidator(this.drawingsToBeSaved, this.drawingImageIndex), Validators.required]);
  }

  private initializeDrawingForm(): void {
    this.setFormFields();

    if (this.isDrawingTypeBmp()) {
      this.setFormFieldsForBmpDrawing();
    }
  }

  private initializeDrawingFormValuesFromDrawing(): void {
    this.setValuesForFormFields();
    this.drawingForm.controls.shape.markAsTouched();

    if (this.isDrawingTypeBmp()) {
      this.setValuesForBmpDrawingFormFields();
    }
  }

  public canExpand(): boolean {
    return (this.drawingForm.controls.configurations?.length ?? 0) > 0;
  }

  public initializeDrawingFormValuesFromDrawingImage(): void {
    this.drawingForm.controls.name.setValue(this.drawingImage.name);

    if (this.isDrawingTypeBmp()) {
      this.drawingForm.controls.heightInPx.setValue(this.drawingImage.dimensionsInPx.heightInPx);
      this.drawingForm.controls.widthInPx.setValue(this.drawingImage.dimensionsInPx.widthInPx);
      if (!AssertionUtils.isNullOrUndefined(this.drawingImage.uploadDrawingInfo?.threadByThread)) {
        this.drawingForm.controls.threadByThread.setValue(this.drawingImage.uploadDrawingInfo.threadByThread);
      }
      if (!AssertionUtils.isNullOrUndefined(this.drawingImage.uploadDrawingInfo?.nrColumnsForWeftSelection)) {
        this.drawingForm.controls.nrColumnsForWeftSelection.setValue(this.drawingImage.uploadDrawingInfo.nrColumnsForWeftSelection);
      }
    }
    this.drawingForm.controls.drawingImage.setValue(this.drawingImage);
    this.setAsyncValidatorForDrawingNameControl();
  }

  private subscribeToNameValueChanges(): void {
    this.drawingForm.controls.name.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.unSubscribeOnViewDestroy)).subscribe((name: string) => {
      if (this.overwriteDuplicateNames) {
        this.drawingsService
          .isIdentifierAvailable(name)
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe((available: boolean) => {
            this.currentDrawingImageWillBeOverwritten = !available;
          });
      }

      if (this.isDrawingTypeBmp()) {
        this.drawingForm.controls.configurations.clear();
        this.getDrawingInfo(name);
      }
    });

    if (!this.overwriteDuplicateNames) {
      this.currentDrawingImageWillBeOverwritten = false;
    }
  }

  private getDrawingInfo(name: string): void {
    this.drawingsService
      .getDrawingInfoByName(this.drawingImage.dimensionsInPx, this.drawingImage.id, name)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe({
        next: (drawingInfo: DrawingInfo) => {
          if (!AssertionUtils.isNullOrUndefined(drawingInfo)) {
            this.drawingForm.controls.code.setValue(drawingInfo.code);
            this.drawingForm.controls.shape.setValue(drawingInfo.shape);
            if (!isEmpty(drawingInfo.configurations)) {
              this.setDrawingConfiguration(drawingInfo.configurations);
            }
          }
        },
        error: () => noop()
      });
  }

  private setDrawingConfiguration(configurations: DrawingConfigurationWithColorAndYarnSet[]): void {
    forEach(configurations, (configuration: DrawingConfigurationWithColorAndYarnSet) => {
      this.drawingForm.controls.configurations.push(
        this.formBuilder.group({
          quality: this.formBuilder.control(configuration.quality, Validators.required),
          colorSet: this.formBuilder.control(configuration.colorSet),
          yarnSet: this.formBuilder.control(configuration.yarnSet),
          mainColorsCreelPosition: this.formBuilder.control(configuration.mainColorsCreelPosition),
          borderColorsCreelPosition: this.formBuilder.control(configuration.borderColorsCreelPosition),
          image: this.formBuilder.control({id: this.drawingImage.id, data: null} as DrawingImage, Validators.required),
          heightInCm: this.formBuilder.control(
            convertToCommercialUnitCentimeter({
              unit: Unit.MILLIMETER,
              value: configuration.commercialDimensionsInMm.heightInMM
            }),
            Validators.min(0)
          ),
          widthInCm: this.formBuilder.control(
            convertToCommercialUnitCentimeter({
              unit: Unit.MILLIMETER,
              value: configuration.commercialDimensionsInMm.widthInMM
            }),
            Validators.min(0)
          )
        }) as ConfigurationForm
      );
    });
  }

  private removeAsyncValidatorForDrawingNameControl(): void {
    if (this.overwriteDuplicateNames) {
      this.drawingsService
        .isIdentifierAvailable(this.drawingImage.name)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((available: boolean) => {
          this.currentDrawingImageWillBeOverwritten = !available;
        });
      if (this.overwriteDuplicateNames) {
        this.drawingForm.controls.name.setAsyncValidators(null);
      }
    } else {
      this.currentDrawingImageWillBeOverwritten = false;
    }
  }
}
